import { useEffect, useState } from "react";
import { clerkEnabled } from "@/lib/clerk";
import { fetchSignupMode } from "@/lib/signup-mode";
import JoinWaitlist from "@/pages/JoinWaitlist";
import { Suspense, lazy } from "react";
// Lazy for the same reason as ClerkGate: no key, no SDK — not in the bundle
// and not resolved by Jest.
const ClerkAuthPanel = lazy(() => import("@/components/auth/ClerkAuthPanel"));
import Stage from "@/components/landing/Stage";
import Logomark from "@/components/Logomark";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/notices";
import { ArrowRight } from "lucide-react";
import NoticeBar from "@/components/NoticeBar";
import { rememberDoor } from "@/lib/doors";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // The invitation email links here as /signup?invite=<token>&email=<address>.
  const inviteToken = params.get("invite") || "";
  // A `+` in an address ("dana+jobs@") arrives as a space when the link was
  // built without encoding — and links like that were sent. No address can
  // contain a space, so a space here can only ever have been a plus.
  const invitedEmail = (params.get("email") || "").trim().replace(/ /g, "+");
  // /signup?door=early from a door's landing page: noted now, read by
  // onboarding after the account exists.
  useEffect(() => { rememberDoor(params.get("door")); }, [params]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // null while unknown. Rendering the signup form first and swapping it for the
  // waiting list a moment later shows people a door that is not there.
  const [inviteOnly, setInviteOnly] = useState(null);

  useEffect(() => {
    let live = true;
    fetchSignupMode().then((m) => { if (live) setInviteOnly(m.inviteOnly); });
    return () => { live = false; };
  }, []);

  // AFTER every hook, deliberately. An early return above them makes the
  // hooks conditional — React requires the same hooks in the same order on
  // every render, and eslint fails the build for it. The legacy state is
  // simply unused when Clerk is on, which costs nothing.
  // Invite-only: there is no signup to offer, so offer the waiting list. The
  // server refuses /auth/register in this mode regardless — this is the page
  // agreeing with it rather than presenting a form doomed to 403.
  if (inviteOnly === null) return null;
  // An invitation is exactly the permission the waiting list exists to grant,
  // so a link that carries one must reach the signup form. Without this the
  // invited person is asked to join the list they were just invited off.
  if (inviteOnly && !inviteToken) return <JoinWaitlist />;

  if (clerkEnabled()) {
    return (
      <Suspense fallback={null}>
        <ClerkAuthPanel mode="sign-up" />
      </Suspense>
    );
  }

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, inviteToken);
      toast.success("Account created!");
      navigate("/onboarding");
    } catch (err) {
      // A 403 here means the server is invite-only and this address has no
      // invitation. The page asked /auth/signup-mode before rendering, so
      // seeing this means that check did not reach the server — a wrong
      // REACT_APP_BACKEND_URL, a proxy, a deploy where the two halves differ.
      //
      // Whatever the cause, the person in front of us just filled in a form
      // that was never going to work. Show them the waiting list rather than
      // an error they cannot act on: the server's own answer is more
      // authoritative than the check we made before rendering.
      if (err?.response?.status === 403) {
        setInviteOnly(true);
        toast.error(err?.response?.data?.detail
          || "NextOpp is invite-only right now. Join the waiting list below.");
        return;
      }
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      <Stage phase={0.15} className="hidden lg:block" contentClassName="h-full flex flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2">
          <Logomark />
          <span className="font-heading font-bold tracking-tight text-lg">NextOpp</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter leading-[1] max-w-md">
            Every application, <em className="font-display not-italic text-dawn-300">tailored</em>.
          </h2>
          <ul className="mt-8 space-y-3 text-white/70 text-sm max-w-md">
            {[
              "Match your resume to any job in seconds",
              "Get missing ATS keywords and clear recommendations",
              "Generate tailored resumes and cover letters",
              "Track every application in one pipeline",
            ].map((t) => <li key={t}>— {t}</li>)}
          </ul>
        </div>
        <div className="u-label text-white/45">Trusted by career-minded professionals</div>
      </Stage>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <Logomark />
              <span className="font-heading font-bold tracking-tight text-lg">NextOpp</span>
            </Link>
          </div>
          <NoticeBar className="top-0 mb-6" />
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600 mb-2">CREATE ACCOUNT</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-stone-950">Start tailoring in minutes</h1>
          {inviteToken ? (
            <p className="mt-2 text-sm text-emerald-800" data-testid="signup-invited-note">
              You've been invited — pick a password and you're in.
            </p>
          ) : (
            <p className="mt-2 text-sm text-stone-600">Free to start. No credit card required.</p>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-stone-900 mb-1.5 block">Full name</Label>
              <Input id="name" required value={name} onChange={(e)=>setName(e.target.value)} className="rounded-sm" data-testid="signup-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-stone-900 mb-1.5 block">Email</Label>
              {/* The token is checked against this exact address on the
                  server, so a forwarded link is not a spare key — and editing
                  the field here could only produce a 403 nobody could read. */}
              <Input id="email" type="email" required value={email}
                     onChange={(e)=>setEmail(e.target.value)}
                     readOnly={Boolean(inviteToken && invitedEmail)}
                     className={`rounded-sm ${inviteToken && invitedEmail ? "bg-paper text-stone-700" : ""}`}
                     data-testid="signup-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-stone-900 mb-1.5 block">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)} className="rounded-sm" data-testid="signup-password-input" />
              <div className="text-xs text-stone-600 mt-1">At least 6 characters.</div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-brand hover:bg-brand-hover text-white rounded-sm font-medium" data-testid="signup-submit-button">
              {loading ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-sand" />
            <span className="text-xs uppercase tracking-[0.2em] text-stone-600 font-bold">or</span>
            <div className="flex-1 h-px bg-sand" />
          </div>

          <button
            onClick={handleGoogle}
            className="w-full h-11 border border-sand rounded-sm hover:bg-paper flex items-center justify-center gap-3 text-sm font-medium transition-colors"
            data-testid="signup-google-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>

          <div className="mt-8 text-sm text-stone-600 text-center">
            Already have an account? <Link to="/login" className="text-brand font-medium hover:underline" data-testid="signup-login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
